import { RecorderHelper } from "@core/bussiness";
import * as apis from "@/services/api_record";
import { RuningInLink } from "@/constants";
import timingStore from "@/store/timing.store";
import recordStore from "@/store/record.store";
import { ICustomeDevcie } from "@/types/device";
import appStore from "@/store/app.store";
import { pingBackend } from "@/utils/networkHelper";
import RecordHelper from "../RecordHelper";
import { formatDocTc } from "@/utils/formatDocTc";
import { setNewSpeakerAvatar } from "@/utils/newAvatar";
import { message } from "antd";

const RecordHadStoppedErrCode = 40002115;
const RecordSwitchCompanyErrCode = 40002104;
const recordHelper = new RecorderHelper();

class MeetingHandler {
  private static _networkErrorHandler: () => void;
  constructor() {
    throw new Error("RecordHandler is a static class");
  }

  /**
   * 初始化录音相关的信息和事件
   */
  private static _initRecorder = () => {
    recordHelper.on("error", (error) => {
      this._handleRecordError(error);
    });
    recordHelper.on("message", (message) => {
      RecordHelper.addRecord(message);
    });
  };

  static listenMessage = (cb: () => void) => {
    recordHelper.on("message", cb);
    return () => {
      recordHelper.off("message", cb);
    };
  };

  static listenDeviceDisconnected = (cb: (error: Error) => void) => {
    recordHelper.on("deviceDisconnected", cb);
    return () => {
      recordHelper.off("deviceDisconnected", cb);
    };
  };

  static registerNetworkErrorHandler = (networkErrorHandler: () => void) => {
    this._networkErrorHandler = networkErrorHandler;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private static _handleRecordError = async (error: any) => {
    console.log("recordHelper error", error);
    // 捕获到错误时，去ping一下后台服务，判断是不是network error异常，不是的话就证明网络正常
    const isOnline = await pingBackend();
    if (!isOnline) {
      this._networkErrorHandler && this._networkErrorHandler();
    } else {
      const errMsg = error?.message || "未知异常";
      message.error(`录制已暂停: ${errMsg}`);
    }
    MeetingHandler.pauseRecord();
  };

  /**
   * 取消录音相关的信息和事件
   */
  private static _uninitRecorder = () => {
    try {
      recordHelper.removeAllListeners();
    } catch (error) {
      console.log("_uninitRecorder error", error);
    }
  };

  private static _accessPermission = async () => {
    try {
      if (RuningInLink) {
        const { data } = await window.LinkService.request("/api/main/v1/system/accessMediaPermission", "microphone");
        console.log("[RunInLink] accessPermission", data);
        if (data === "denied") {
          const error = new Error("应用无麦克风权限，请开启权限后重试");
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (error as any).code = "epermission";
          throw error;
        }
      } else {
        const permission = await recordHelper.getPermission();
        console.log("accessPermission", permission);
        if (!permission) {
          const error = new Error("应用无麦克风权限，请开启权限后重试");
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (error as any).code = "epermission";
          throw error;
        }
      }
    } catch (error) {
      console.log("_accessPermission error", error);
      throw error;
    }
  };

  /**
   * 检测位置权限
   */
  public static checkLocation = async () => {
    try {
      if (RuningInLink) {
        const { data } = await window.LinkService.request("/api/process/v1/screenShare/checkLocationAccess");
        return data;
      }
      return true;
    } catch (error) {
      console.log("checkLocation error", error);
      return true; // 定位权限出问题时，返回true，定位权限不影响录制，只是扫描不出列表
    }
  };

  /**
   * 是否已经请求过位置权限弹框
   */
  public static isRequested = () => {
    const res = localStorage.getItem("locationIsRequested");
    if (res === null) {
      localStorage.setItem("locationIsRequested", "true");
      return false;
    }
    return res === "true";
  };

  public static requestLocation = async () => {
    try {
      if (RuningInLink) {
        await window.LinkService.request("/api/process/v1/screenShare/requestLocationAccess");
      }
    } catch (error) {
      console.log("requestLocation error", error);
    }
  };

  public static openLocationSetting = async () => {
    try {
      if (RuningInLink) {
        await window.LinkService.request("/api/process/v1/screenShare/openLocationPreference");
        console.log("openLocationSetting request success");
      }
    } catch (error) {
      console.log("openLocationSetting error", error);
    }
  };

  /**
   * 开始录音
   * @returns
   */
  public static startRecord = async () => {
    try {
      // 获取录音权限
      await this._accessPermission();
      // 初始化录音相关的信息和事件
      this._initRecorder();
      // 获取录音相关的信息
      const asrModelId = appStore.getState().asrModelId;
      const voiceSamplingRate =
        localStorage.getItem("meetingVoiceSamplingRate") || "70";
      const res = await apis.startMeetingRecord({
        languageModelId: asrModelId,
        future: 3,
        thresholdScore: parseInt(voiceSamplingRate),
        useMarkDownSummary: true,
        audioFormat: "mp3",
      });
      const { streamName, wssPushUrl, title } = res.data;
      recordStore.getState().setStreamName(streamName);
      recordStore.getState().setMeetingName(title);
      await recordHelper.start({
        streamName,
        wssPushUrl,
      });
      timingStore.getState().startTiming();
      return true;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.log("startRecord error", error.code);
      const msg = error?.message || "启动录制失败";
      if (error.errCode === 40002131) {
        message.error('一天内支持最长10小时实时记录，已超时');
        return false;
      }
      message.error(msg);
      return false;
    }
  };


  /**
   * 获取当前拾音大板的设备名称信息
   */
  public static getCurrentWifiInfo = async () => {
    return recordHelper.getCurrentWifiInfo();
  };

  /**
   * 暂停录制
   */
  public static pauseRecord = async () => {
    try {
      await MeetingHandler.saveDoc();
      await recordHelper.pause();
      timingStore.getState().pauseTiming();
      return true;
    } catch (error: any) {
      console.log("pauseRecord error", error);
      // 断网
      if (error?.errCode === 'ERR_NETWORK') {
        timingStore.getState().pauseTiming();
        return true;
      }
      return false;
    }
  };

  /**
   * 继续录制
   * @returns boolean
   */
  public static resumeRecord = async () => {
    try {
      await recordHelper.continue();
      timingStore.getState().startTiming();
      return true;
    } catch (error) {
      console.log("continueRecord error", error);
      return false;
    }
  };

    /**
   * 结束录制
   * @returns boolean
   */
    public static stopRecordWithoutPermission = async () => {
      try {  
        await recordHelper.stop();
        timingStore.getState().stopTiming();
        this._uninitRecorder();
      } catch (error: any) {
        console.log("stopRecordWithoutPermission error", error);
      }
    };

  /**
   * 结束录制
   * @returns boolean
   */
  public static stopRecord = async () => {
    try {
      await MeetingHandler.saveDoc();
      await recordHelper.stop();
      timingStore.getState().stopTiming();
      this._uninitRecorder();
      await apis.stopMeetingRecord(recordStore.getState().streamName);
      return true;
    } catch (error: any) {
      console.log("stopRecord error", error);
      if (error?.errCode === RecordSwitchCompanyErrCode) {
        message.error('当前账号已经被切换企业，请切回原来的企业');
        return false;
      }
      // 如果录制已经停止，则允许结束
      if (error?.errCode === RecordHadStoppedErrCode) {
        return true;
      }

      message.error("结束录制失败, 请重试");
      return false;
    }
  };

  /**
   * 切换麦克风
   */
  public static switchMicrophone = (mic: MediaDeviceInfo) => {
    return recordHelper.switchMicrophone(mic);
  };

  /**
   * 切换设备
   */
  public static switchDevice = async (device: ICustomeDevcie) => {
    console.log("switchDevice", device);
    // 如果切换到本地录制，则需要是使用webrtc
    const type = device.type === "computer" ? "computer" : "screenShare";
    const data = device.type === "computer" ? undefined : device.extra;
    return recordHelper.switchDevice(type, data);
  };

  /**
   * 清空录制数据
   */
  public static clearRecordStore = async () => {
    timingStore.getState().resetStore();
    recordStore.getState().resetStore();
  };

  /**
   * 保存原文
   */
  public static saveDoc = async () => {
    const lastRecord = recordStore.getState().lastRecord;
    // 判断最后一条句子是否有稳态，没有的话，就不添加到原文中
    const lastRecordSpeakContent = lastRecord?.sc?.flatMap((scItem) => scItem.words).map((wordItem) => wordItem.word).join("");
    const speakerName = ['发言人'];
    const { avatar, role } = setNewSpeakerAvatar(speakerName[0]);
    const speakerInfo = {
      speakerName,
      headImage: avatar,
      useDefaultIcon: true,
      role,
    }
    const formatRecords = (lastRecord && lastRecordSpeakContent?.length)
      ? [...recordStore.getState().records, { ...lastRecord, ...speakerInfo }]
      : recordStore.getState().records;

    await apis.saveDoc({
      streamName: recordStore.getState().streamName,
      srcDoc: {
        pg: formatDocTc(formatRecords),
      },
    });
    recordStore.getState().setShowSaveDocTip(true);
  };
}
export default MeetingHandler;
